import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/couture/Eyebrow";
import { BEFORE_AFTER_CASES, BEFORE_AFTER_CATEGORIES, type BeforeAfterCase, type BeforeAfterCategory } from "@/lib/before-after-data";

interface BeforeAfterCarouselProps {
  category?: BeforeAfterCategory;
  cases?: BeforeAfterCase[];
  showFilters?: boolean;
  eyebrow?: string;
  heading?: string;
  subheading?: string;
  className?: string;
}

const BeforeAfterCarousel = ({ category, cases: suppliedCases, showFilters = false, eyebrow = "SMILE TRANSFORMATIONS", heading = "Real patients. Real results.", subheading, className }: BeforeAfterCarouselProps) => {
  const [activeFilter, setActiveFilter] = useState<"All" | BeforeAfterCategory>("All");
  const cases = suppliedCases ?? BEFORE_AFTER_CASES.filter((photo) => category ? photo.category === category : activeFilter === "All" || photo.category === activeFilter);
  const [emblaRef, embla] = useEmblaCarousel({ loop: cases.length > 1, align: "center" });
  const [thumbsRef, thumbs] = useEmblaCarousel({ containScroll: "keepSnaps", dragFree: true });
  const [selected, setSelected] = useState(0);
  const caseKey = cases.map((photo) => photo.id).join(",");

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => {
      setSelected(embla.selectedScrollSnap());
      thumbs?.scrollTo(embla.selectedScrollSnap());
    };
    embla.on("select", onSelect);
    embla.on("reInit", onSelect);
    onSelect();
    return () => { embla.off("select", onSelect); embla.off("reInit", onSelect); };
  }, [embla, thumbs]);

  useEffect(() => {
    embla?.reInit({ loop: cases.length > 1 });
    embla?.scrollTo(0, true);
    thumbs?.scrollTo(0, true);
    setSelected(0);
  }, [caseKey, embla, thumbs]);

  if (!cases.length) return null;
  const currentCase = cases[selected] ?? cases[0];

  return (
    <section className={cn("bg-background py-12 md:py-20", className)} aria-label="Before and after treatment photos">
      <div className="container">
        <div className="text-center mb-8 md:mb-10">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="font-heading text-3xl md:text-4xl font-light mt-4 mb-4 text-primary">{heading}</h2>
          <div className="gold-hairline mx-auto" />
          {subheading && <p className="text-muted-foreground max-w-2xl mx-auto mt-5 font-body">{subheading}</p>}
        </div>
        {showFilters && !category && !suppliedCases && (
          <div className="flex flex-wrap justify-center gap-2 mb-6" aria-label="Treatment filters">
            {BEFORE_AFTER_CATEGORIES.map((cat) => (
              <Button key={cat} variant={activeFilter === cat ? "secondary" : "ghost"} onClick={() => setActiveFilter(cat)} aria-pressed={activeFilter === cat} className="h-auto min-h-11 px-3 py-2 text-xs whitespace-normal tracking-normal shadow-none">{cat}</Button>
            ))}
          </div>
        )}
        <div className="max-w-[640px] mx-auto">
          <div className="overflow-hidden" ref={emblaRef} aria-roledescription="carousel">
            <div className="flex touch-pan-y">
              {cases.map((photo, index) => (
                <div key={photo.id} className="min-w-0 flex-[0_0_100%]" role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${cases.length}`}>
                  <div className="aspect-square bg-background">
                    <img src={photo.image} alt={`${photo.treatment} — before and after at Mutha's Advanced Dental Care`} className="block w-full h-full object-contain" width={800} height={800} loading="lazy" decoding="async" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 mt-4 min-h-12">
            <Button variant="outline" size="icon" onClick={() => embla?.scrollPrev()} aria-label="Previous transformation" title="Previous transformation" disabled={cases.length < 2} className="h-11 w-11 shrink-0 shadow-none"><ChevronLeft /></Button>
            <div className="text-center min-w-0" aria-live="polite" aria-atomic="true">
              <p className="font-heading text-sm md:text-base text-primary">{currentCase.treatment}</p>
              <p className="text-xs text-muted-foreground tabular-nums">{selected + 1} / {cases.length}</p>
            </div>
            <Button variant="outline" size="icon" onClick={() => embla?.scrollNext()} aria-label="Next transformation" title="Next transformation" disabled={cases.length < 2} className="h-11 w-11 shrink-0 shadow-none"><ChevronRight /></Button>
          </div>
          {currentCase.caption && <p className="text-sm text-muted-foreground text-center mt-3">{currentCase.caption}</p>}
          {cases.length > 1 && (
            <div className="mt-5 overflow-hidden" ref={thumbsRef} aria-label="Treatment photo thumbnails">
              <div className="flex gap-2">
                {cases.map((photo, index) => (
                  <Button key={photo.id} variant="ghost" onClick={() => embla?.scrollTo(index)} aria-label={`View ${photo.treatment} case ${index + 1}`} aria-pressed={selected === index} className={cn("h-auto p-0 flex-[0_0_22%] md:flex-[0_0_15%] aspect-square rounded-none border-2 overflow-hidden shadow-none hover:bg-transparent", selected === index ? "border-primary" : "border-transparent")}>
                    <img src={photo.image} alt="" aria-hidden="true" className="block w-full h-full object-contain" loading="lazy" decoding="async" width={800} height={800} />
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default BeforeAfterCarousel;
